/**
 * A colour per epic, so a theme can be told at a glance down a column.
 *
 * DRAWN FROM THE AREA / EPIC IDENTITY PALETTE, NOT THE STATUS INKS.
 *
 * This once tinted from the eight status tokens (done-green, critical-red,
 * in-progress-amber…), because the standing rule was to invent no new colour and
 * the status set was all there was. It worked, but every epic chip carried a
 * faint status cast — a green chip read a little like "done" — and eight colours
 * at two strengths was the ceiling. When areas got their own sixteen-colour
 * identity palette (src/area-colours.ts, #462), epics moved onto the SAME set:
 * one consistent object-colour system, and no status overlap. Sixteen hues at
 * two strengths is thirty-two chips anybody can tell apart, and still not one
 * value invented here — they are the --rgb-a1..a16 tokens the area palette
 * defines. Past thirty-two it repeats, which is honest.
 *
 * WHY IT IS HASHED AND NOT COUNTED
 *
 * Assigning colours in the order epics appear means every colour on the board
 * changes the day somebody adds an epic early in the alphabet. Hashing the name
 * makes a given epic the same colour for ever, on every screen, for everybody.
 *
 * COLOUR IS NEVER THE ONLY THING
 *
 * The chip still carries its name. That matters more than usual here: epic
 * names on this board run past twenty characters and truncate, so if two
 * truncate alike the tint would be all that separated them — and colour alone
 * is what this board never relies on.
 */

/** The sixteen tintable identity colours, by the token that holds their components. */
export const EPIC_HUES = [
  'a1', 'a2', 'a3', 'a4', 'a5', 'a6', 'a7', 'a8',
  'a9', 'a10', 'a11', 'a12', 'a13', 'a14', 'a15', 'a16',
] as const;

/**
 * Faint and less faint. Kept apart from the hues so the count is obvious.
 *
 * The stronger one is 0.18 — the most the identity palette allows, found by
 * computing it rather than choosing it. The binding case is no longer a warm red
 * on a light chip but the brightest hue (teal, a6) on the DARK card: a bright
 * tint lifts the near-black ground enough to pull the chip's own light-grey text
 * down, and at 0.20 it measured 4.36 against the 4.5 bar. 0.18 clears it at 4.54,
 * re-checked on every one of the thirty-two chips in both themes by
 * test-contrast.ts rather than taken on faith.
 */
export const EPIC_STRENGTHS = [0.1, 0.18] as const;

export interface EpicTint {
  /** The custom property holding this colour's components. */
  rgb: string;
  alpha: number;
}

/**
 * A small, stable string hash.
 *
 * Not for security — only to spread names across the slots in a way that does
 * not change between sessions, machines or people.
 */
function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

const SLOTS = EPIC_HUES.length * EPIC_STRENGTHS.length;

const ofSlot = (slot: number): EpicTint => ({
  rgb: `--rgb-${EPIC_HUES[slot % EPIC_HUES.length]}`,
  alpha: EPIC_STRENGTHS[Math.floor(slot / EPIC_HUES.length)],
});

/**
 * The tint for one epic, given every epic on the board.
 *
 * WHY THE WHOLE LIST IS NEEDED
 *
 * Hashing a name alone gives a colour that never changes — but nine names
 * hashed into sixteen slots collide, and on this board two of the nine did. A
 * feature whose entire purpose is telling epics apart cannot shrug at two of
 * them being identical.
 *
 * So a collision is resolved by taking the next free slot, walking the epics in
 * a fixed order. Every epic still starts from its own name, so the common case
 * is unchanged and stable; only the ones that genuinely clash move, and only
 * relative to each other.
 *
 * That is the trade: adding an epic can shift the colour of one that collides
 * with it, but never of the whole board — which is what assigning colours by
 * position would have done every time somebody added one early in the alphabet.
 */
export function epicTint(epic: string | undefined | null, all: string[] = []): EpicTint | null {
  const name = (epic ?? '').trim();
  if (!name) return null;

  // A fixed order, so the same set of epics always resolves the same way
  // whatever order they arrived in.
  const board = [...new Set(all.map((e) => e.trim()).filter(Boolean))].sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase())
  );
  const taken = new Set<number>();
  for (const e of board) {
    let slot = hash(e.toLowerCase()) % SLOTS;
    // More epics than slots: past sixteen it has to repeat, and repeating is
    // better than looping for ever looking for a space that does not exist.
    for (let n = 0; n < SLOTS && taken.has(slot); n++) slot = (slot + 1) % SLOTS;
    taken.add(slot);
    if (e.toLowerCase() === name.toLowerCase()) return ofSlot(slot);
  }
  // Not on the board — colour it from its own name alone.
  return ofSlot(hash(name.toLowerCase()) % SLOTS);
}

/** The chip's background, ready to hand to a style attribute. */
export function epicBackground(epic: string | undefined | null, all: string[] = []): string | undefined {
  const t = epicTint(epic, all);
  return t ? `rgba(var(${t.rgb}), ${t.alpha})` : undefined;
}
